// components/products/sections/BasicInfoSection.jsx
import React, { useMemo } from "react";
import { Info, Package2 } from "lucide-react";
import Dropdown from "components/common/Dropdown";
import CategoryHierarchyDropdown from "pages/Category/components/CategoryHierarchyDropdown";

const BasicInfoSection = ({
  formData,
  handleChange,
  errors,
  mode,
  PRODUCT_TYPE_ENUM,
  PRODUCT_SUBTYPE_ENUM,
  PRODUCT_GENDER_ENUM,
  categories,
}) => {
  // Format options for Dropdown components
  const typeOptions = PRODUCT_TYPE_ENUM.map((type) => ({
    _id: type,
    name: type,
  }));

  const subtypeOptions = PRODUCT_SUBTYPE_ENUM.map((subtype) => ({
    _id: subtype,
    name: subtype.replace(/_/g, " "),
  }));

  const genderOptions = PRODUCT_GENDER_ENUM.map((gender) => ({
    _id: gender,
    name: gender,
  }));

  // Handle category selection from CategoryHierarchyDropdown
  const handleCategorySelect = (categoryId) => {
    try {
      if (!categoryId) {
        handleChange({ target: { name: "categories", value: [] } });
        return;
      }

      // For single selection, wrap in array since formData.categories expects array
      handleChange({ target: { name: "categories", value: [categoryId] } });
    } catch (error) {
      console.error("Error in category selection:", error);
      handleChange({ target: { name: "categories", value: [] } });
    }
  };

  // Get the first selected category ID for single-select dropdown
  const selectedCategoryId = useMemo(() => {
    if (
      !formData.categories ||
      !Array.isArray(formData.categories) ||
      formData.categories.length === 0
    ) {
      return null;
    }
    return formData.categories[0];
  }, [formData.categories]);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Basic Information
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Product ID (read-only in edit mode) */}
        {mode === "edit" && formData.productId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product ID
            </label>
            <div className="flex items-center">
              <Package2 className="w-4 h-4 text-gray-400 mr-2" />
              <input
                type="text"
                value={formData.productId}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
              />
            </div>
          </div>
        )}

        {/* SKU */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            SKU
            {mode === "add" && (
              <span className="text-xs text-gray-500 ml-2">
                (Leave empty for auto-generation)
              </span>
            )}
          </label>
          <input
            type="text"
            name="sku"
            value={formData.sku}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder={
              mode === "add" ? "Auto-generate or enter custom SKU" : "SKU"
            }
            readOnly={mode === "edit" && formData.sku}
          />
          {mode === "add" && (
            <p className="mt-1 text-xs text-gray-500">
              Format: {formData.type?.substring(0, 2)}
              {formData.purity?.replace(/[^0-9]/g, "")}-XXXXXX
            </p>
          )}
        </div>

        {/* Product Name */}
        <div className={mode === "edit" ? "" : "md:col-span-2"}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product Name *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${
              errors.name ? "border-red-300" : "border-gray-300"
            } rounded-md focus:ring-blue-500 focus:border-blue-500`}
            placeholder="Enter product name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Type */}
        <div>
          <Dropdown
            label="Product Type *"
            options={typeOptions}
            selected={
              formData.type ? { _id: formData.type, name: formData.type } : null
            }
            onChange={(option) =>
              handleChange({ target: { name: "type", value: option._id } })
            }
            error={errors.type}
            placeholder="Select Type"
          />
        </div>

        {/* Subtype */}
        <div>
          <Dropdown
            label="Product Subtype *"
            options={subtypeOptions}
            selected={
              formData.subType
                ? {
                    _id: formData.subType,
                    name: formData.subType.replace(/_/g, " "),
                  }
                : null
            }
            onChange={(option) =>
              handleChange({ target: { name: "subType", value: option._id } })
            }
            error={errors.subType}
            placeholder="Select Subtype"
          />
        </div>

        {/* Gender */}
        <div>
          <Dropdown
            label="Gender"
            options={genderOptions}
            selected={
              formData.gender
                ? { _id: formData.gender, name: formData.gender }
                : null
            }
            onChange={(option) =>
              handleChange({ target: { name: "gender", value: option._id } })
            }
            placeholder="Select Gender"
          />
        </div>

        {/* Categories - Using CategoryHierarchyDropdown */}
        <div>
          <CategoryHierarchyDropdown
            label="Category"
            categories={categories || []}
            value={selectedCategoryId}
            onChange={handleCategorySelect}
            placeholder="Select Category"
            showFullPathInList={true}
            allowClear={true}
            error={errors.categories}
          />
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Product description"
          />
        </div>

        {/* Narration (Legacy field) */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Narration
            <span className="text-xs text-gray-500 ml-2">(Optional note)</span>
          </label>
          <input
            type="text"
            name="narration"
            value={formData.narration}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Any additional notes or narration"
          />
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSection;
